import React, { memo, useCallback } from 'react';
import { Box, Checkbox } from '@mui/material';

/**
 * Optimized virtual DataGrid row component
 * Handles individual row rendering with minimal re-renders
 */
const VirtualDataGridRow = memo(({
  row,
  columns,
  rowHeight,
  top,
  checkboxSelection,
  isSelected,
  onRowClick,
  onSelectionChange,
  getRowId
}) => {
  const rowId = getRowId(row);

  // Handle row click
  const handleRowClick = useCallback((e) => {
    if (e.target.type === 'checkbox') return; // Don't trigger on checkbox click
    onRowClick?.(row, e);
  }, [row, onRowClick]);

  // Handle checkbox change
  const handleCheckboxChange = useCallback((e) => {
    e.stopPropagation();
    onSelectionChange(e.target.checked);
  }, [onSelectionChange]);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: `${top}px`,
        left: 0,
        right: 0,
        height: `${rowHeight}px`,
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
        cursor: onRowClick ? 'pointer' : 'default',
        transform: 'translateZ(0)', // GPU acceleration
        '&:hover': {
          backgroundColor: isSelected
            ? 'rgba(25, 118, 210, 0.12)'
            : 'rgba(0, 0, 0, 0.04)'
        }
      }}
      onClick={handleRowClick}
    >
      {/* Checkbox column */}
      {checkboxSelection && (
        <Box
          sx={{
            width: 52,
            minWidth: 52,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pl: 1
          }}
        >
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={handleCheckboxChange}
            inputProps={{ 'aria-label': `Select row ${rowId}` }}
          />
        </Box>
      )}

      {/* Data columns */}
      {columns.map((column) => (
        <VirtualDataGridCell
          key={column.field}
          column={column}
          row={row}
          rowHeight={rowHeight}
        />
      ))}
    </Box>
  );
});

/**
 * Individual cell component with memoization
 */
const VirtualDataGridCell = memo(({ column, row, rowHeight }) => {
  const value = row[column.field];

  // Handle custom renderCell
  const cellContent = column.renderCell
    ? column.renderCell({ value, row, field: column.field })
    : (value ?? '');

  // Flex columns grow to fill remaining horizontal space; others are fixed width
  const sizing = column.flex
    ? { flex: column.flex, minWidth: column.minWidth || 0 }
    : {
        width: column.width,
        minWidth: column.minWidth || column.width,
        maxWidth: column.maxWidth || column.width,
      };

  return (
    <Box
      sx={{
        ...sizing,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        px: column.disablePadding ? 0.5 : 2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '0.875rem',
        borderRight: '1px solid rgba(224, 224, 224, 1)',
        '&:last-child': {
          borderRight: 'none'
        }
      }}
      title={typeof cellContent === 'string' ? cellContent : undefined}
    >
      {cellContent}
    </Box>
  );
});

VirtualDataGridRow.displayName = 'VirtualDataGridRow';
VirtualDataGridCell.displayName = 'VirtualDataGridCell';

export default VirtualDataGridRow;